import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { abrir, aparecer, desplegar, fila } from '../components/movimiento';
import { api } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { Cambia, Cargando, Encabezado, Icono, Numero, PanelError } from '../components/ui';
import { aviso, botonSecundario, c, campo, etiquetaCampo, miles, tarjeta } from '../theme';

/**
 * Mantenimiento: vaciar tablas a propósito.
 *
 * El importador de inventario es de ida sola. Subir la planilla de ejemplo para
 * ver cómo funciona deja el maestro con un artículo inventado y dos pallets, y
 * hasta ahora la única salida era entrar a la base por SQL.
 *
 * Toda la pantalla está armada alrededor de una idea: **que no se pueda borrar
 * sin haber leído qué se borra**. Por eso se muestran las filas de cada tabla
 * antes de tocar nada, por eso el resumen dice lo que se va a llevar de arriba
 * —vaciar el maestro se lleva el stock y las órdenes de trabajo— y por eso hay
 * que tipear una palabra en vez de apretar «sí». Un `confirm()` se acepta sin
 * leer.
 *
 * Lo que no se puede hacer desde acá es desarmar el depósito: los sectores, los
 * racks acomodados y las posiciones con stock se editan en Planta, que revalida
 * la geometría. Lo único de la planta que aparece son las ubicaciones que
 * inventó el importador y que nadie acomodó nunca.
 */


export default function Mantenimiento() {
  const { datos, cargando, error, recargar } = useApi(() => api.limpieza(), [], VIVO.normal);

  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const [tipeado, setTipeado] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [problema, setProblema] = useState('');
  const [hecho, setHecho] = useState<{ nombre: string; filas: number }[] | null>(null);

  const ajustes = useApi(() => api.ajustes(), []);
  const [cambiandoAjuste, setCambiandoAjuste] = useState(false);
  const [errorAjuste, setErrorAjuste] = useState('');

  async function alternarSacarEnteros(prendido: boolean) {
    setCambiandoAjuste(true);
    setErrorAjuste('');
    try {
      await api.cambiarAjustes({ sacarEnteros: prendido });
      await ajustes.recargar();
    } catch (err) {
      setErrorAjuste(mensajeDeError(err));
    } finally {
      setCambiandoAjuste(false);
    }
  }

  // El rollo de etiquetas: lo escrito, hasta que se guarda.
  const [rollo, setRollo] = useState('');
  const [errorRollo, setErrorRollo] = useState('');

  async function guardarRollo() {
    setCambiandoAjuste(true);
    setErrorRollo('');
    try {
      await api.cambiarRollo(Number(rollo));
      await ajustes.recargar();
      setRollo('');
    } catch (err) {
      setErrorRollo(mensajeDeError(err));
    } finally {
      setCambiandoAjuste(false);
    }
  }

  const [descargando, setDescargando] = useState(false);
  const [errorUbicaciones, setErrorUbicaciones] = useState('');

  async function descargarUbicaciones() {
    setDescargando(true);
    setErrorUbicaciones('');
    try {
      await api.descargarUbicaciones();
    } catch (err) {
      setErrorUbicaciones(mensajeDeError(err));
    } finally {
      setDescargando(false);
    }
  }

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando texto="Mirando qué hay cargado…" />;
  if (!datos) return null;

  const porId = new Map(datos.objetivos.map((o) => [o.id, o]));

  // Lo elegido más todo lo que eso se lleva. Es lo que se va a borrar de
  // verdad, y es lo que tiene que decir el cartel.
  const alcance = new Set(elegidos);
  for (const id of elegidos) for (const otro of porId.get(id)?.arrastra ?? []) alcance.add(otro);

  const arrastrados = [...alcance].filter((id) => !elegidos.has(id));
  const filas = [...alcance].reduce((a, id) => a + (porId.get(id)?.filas ?? 0), 0);
  const listo = alcance.size > 0 && tipeado.trim().toUpperCase() === datos.confirmacion;

  function alternar(id: string) {
    setElegidos((antes) => {
      const nuevo = new Set(antes);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });
    setHecho(null);
    setProblema('');
  }

  async function vaciar() {
    setTrabajando(true);
    setProblema('');
    try {
      const r = await api.vaciar([...elegidos], tipeado.trim().toUpperCase());
      setHecho(r.vaciado.map((v) => ({ nombre: v.nombre, filas: v.filas })));
      setElegidos(new Set());
      setTipeado('');
      recargar();
    } catch (err) {
      setProblema(mensajeDeError(err));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        bajada="Vaciar datos cargados por error, como cuando se importa la planilla de ejemplo para probar. No se puede deshacer."
      />

      {/* -------------------------------------------------- interruptores */}
      {ajustes.datos && (
        <motion.div {...aparecer} className="p-4 tablet:p-5" style={{ ...tarjeta, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <Icono nombre="toggle_on" size={20} color={c.texto3} />
          <label htmlFor="ajuste-sacar-enteros" style={{ flex: 1, minWidth: 240, cursor: 'pointer' }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>Botón «Sacar de su posición los enteros»</span>
            <div style={{ marginTop: 4, fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
              En Stock disponible, deja sin ubicar de una a todos los pallets que salen enteros.
              {ajustes.datos.cambiadaPor ? ` Lo cambió por última vez ${ajustes.datos.cambiadaPor}.` : ''}
            </div>
            {errorAjuste && (
              <div role="alert" style={{ ...aviso('err'), marginTop: 10 }}>
                {errorAjuste}
              </div>
            )}
          </label>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: c.texto2 }}>
            <input
              id="ajuste-sacar-enteros"
              type="checkbox"
              checked={ajustes.datos.sacarEnteros}
              disabled={cambiandoAjuste}
              onChange={(e) => void alternarSacarEnteros(e.target.checked)}
              style={{ width: 20, height: 20 }}
            />
            {ajustes.datos.sacarEnteros ? 'Se muestra' : 'Oculto'}
          </span>
        </motion.div>
      )}

      {/* -------------------------------------------------- rollo de etiquetas */}
      {ajustes.datos && (
        <motion.div {...aparecer} className="p-4 tablet:p-5" style={{ ...tarjeta, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <Icono nombre="label" size={20} color={c.texto3} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>
              Rollo de etiquetas: quedan {ajustes.datos.rolloEtiquetas ?? '—'}
            </span>
            <div style={{ marginTop: 4, fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
              Las de 100 × 150 de la térmica. Baja sola al imprimir y el operario lo carga al cambiar el rollo,
              desde Imprimir etiquetas. Si se canceló una impresión, corregilo acá con lo que tiene de verdad.
            </div>
            {errorRollo && (
              <div role="alert" style={{ ...aviso('err'), marginTop: 10 }}>
                {errorRollo}
              </div>
            )}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void guardarRollo();
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <input
              aria-label="Etiquetas que tiene el rollo"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              required
              value={rollo}
              placeholder={String(ajustes.datos.rolloEtiquetas ?? '')}
              onChange={(e) => setRollo(e.target.value)}
              style={{ ...campo, width: 110 }}
            />
            <button type="submit" disabled={cambiandoAjuste || rollo === ''} style={{ ...botonSecundario, minHeight: 40, padding: '0 14px' }}>
              Guardar
            </button>
          </form>
        </motion.div>
      )}

      {/* -------------------------------------------------- guardar ubicaciones */}
      <motion.div {...aparecer} className="p-4 tablet:p-5" style={{ ...tarjeta, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <Icono nombre="download" size={20} color={c.texto3} />
        <div style={{ flex: 1, minWidth: 240 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>Antes de vaciar el stock</span>
          <div style={{ marginTop: 4, fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
            Bajate las ubicaciones de los pallets. Después de vaciar y volver a importar el stock, las restaurás
            desde Cargar inventario.
          </div>
          <AnimatePresence>
            {errorUbicaciones && (
              <motion.div key={errorUbicaciones} {...abrir(10)} role="alert" style={{ ...aviso('err'), marginTop: 10 }}>
                {errorUbicaciones}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <button
          type="button"
          onClick={descargarUbicaciones}
          disabled={descargando}
          style={{ ...botonSecundario, opacity: descargando ? 0.5 : 1 }}
        >
          <Icono nombre="download" size={19} />
          <Cambia clave={descargando ? 'a' : 'b'}>{descargando ? 'Descargando…' : 'Descargar ubicaciones'}</Cambia>
        </button>
      </motion.div>

      <AnimatePresence>
        {problema && (
          <motion.div key={problema} {...abrir(10)} role="alert" style={aviso('err')}>
            {problema}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
      {hecho && (
        <motion.div {...abrir(10)} role="status" style={aviso('ok')}>
          <strong>Listo.</strong>{' '}
          {hecho.every((h) => h.filas === 0)
            ? 'No había nada cargado, así que no se borró nada.'
            : hecho
                .filter((h) => h.filas > 0)
                .map((h) => `${h.nombre.toLowerCase()}: ${miles(h.filas)}`)
                .join(' · ')}
        </motion.div>
      )}
      </AnimatePresence>

      {/* ------------------------------------------------------ qué hay ahora */}
      <motion.div {...aparecer} className="p-4 tablet:p-5" style={{ ...tarjeta, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Qué hay cargado</span>
          <div style={{ marginTop: 4, fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
            Marcá lo que quieras vaciar. Mirar no borra nada.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {datos.objetivos.map((o, i) => {
            const marcado = elegidos.has(o.id);
            const seVaPorArrastre = !marcado && alcance.has(o.id);
            const vacio = o.filas === 0;
            const fondoBorde = marcado || seVaPorArrastre ? c.rojo : c.borde;
            const fondo = marcado ? c.rojoSuave : seVaPorArrastre ? c.ambarSuave : c.panel;

            return (
              <motion.label
                key={o.id}
                {...fila(i)}
                // Marcar o soltar un renglón cambia su borde y su fondo con transición.
                initial={{ opacity: 0, y: 10, borderColor: fondoBorde, backgroundColor: fondo }}
                animate={{
                  opacity: vacio && !marcado && !seVaPorArrastre ? 0.6 : 1,
                  y: 0,
                  borderColor: fondoBorde,
                  backgroundColor: fondo,
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 11,
                  padding: '11px 13px',
                  borderRadius: 9,
                  cursor: 'pointer',
                  borderStyle: 'solid',
                  borderWidth: 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={marcado}
                  onChange={() => alternar(o.id)}
                  style={{ marginTop: 2, width: 17, height: 17, accentColor: c.rojo, flex: '0 0 auto' }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>{o.nombre}</span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        color: vacio ? c.texto4 : c.texto2,
                      }}
                    >
                      {vacio ? 'vacío' : (<><Numero valor={o.filas} desdeCero={false} /> {o.filas === 1 ? 'registro' : 'registros'}</>)}
                    </span>
                    <AnimatePresence initial={false}>
                      {seVaPorArrastre && (
                        <motion.span
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -8 }}
                          style={{ fontSize: 12, fontWeight: 600, color: c.ambar }}
                        >
                          se va con lo que marcaste
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </span>
                  <span style={{ display: 'block', marginTop: 2, fontSize: 12.5, color: c.texto3, textWrap: 'pretty' }}>
                    {o.detalle}
                  </span>
                  {o.arrastra.length > 0 && (
                    <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: c.ambar, textWrap: 'pretty' }}>
                      Vaciar esto se lleva también:{' '}
                      {o.arrastra.map((id) => porId.get(id)?.nombre.toLowerCase() ?? id).join(', ')}. No se puede
                      vaciar sin eso: es lo que depende de esto.
                    </span>
                  )}
                </span>
              </motion.label>
            );
          })}
        </div>
      </motion.div>

      {/* ------------------------------------------------------ la confirmación */}
      <AnimatePresence>
      {alcance.size > 0 && (
        <motion.div
          {...desplegar}
          className="p-4 tablet:p-5"
          style={{
            ...tarjeta,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            borderColor: c.rojo,
          }}
        >
          <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
            <Icono nombre="warning" size={22} color={c.rojo} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>
                Se van a borrar <Numero valor={filas} desdeCero={false} /> {filas === 1 ? 'registro' : 'registros'}
              </span>
              <div style={{ marginTop: 4, fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
                {[...alcance].map((id) => porId.get(id)?.nombre ?? id).join(' · ')}
                {arrastrados.length > 0 && (
                  <>
                    {' '}
                    —{' '}
                    <strong>
                      {arrastrados.map((id) => porId.get(id)?.nombre.toLowerCase() ?? id).join(', ')}
                    </strong>{' '}
                    {arrastrados.length === 1 ? 'no lo marcaste' : 'no los marcaste'}, {' '}
                    {arrastrados.length === 1 ? 'se va' : 'se van'} porque {arrastrados.length === 1 ? 'apunta' : 'apuntan'}{' '}
                    a lo que sí.
                  </>
                )}
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: c.rojo, textWrap: 'pretty' }}>
                No se puede deshacer y no hay copia: lo que se borre se vuelve a cargar a mano o con un CSV.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ ...etiquetaCampo, flex: 1, minWidth: 200 }}>
              Escribí «{datos.confirmacion}» para confirmar
              <input
                value={tipeado}
                onChange={(e) => setTipeado(e.target.value)}
                placeholder={datos.confirmacion}
                autoComplete="off"
                spellCheck={false}
                style={{ ...campo, borderColor: listo ? c.rojo : c.bordeFuerte }}
              />
            </label>
            <motion.button
              type="button"
              onClick={vaciar}
              disabled={!listo || trabajando}
              initial={false}
              animate={{
                borderColor: listo ? c.rojo : c.bordeFuerte,
                color: listo ? c.rojo : c.texto4,
                opacity: listo && !trabajando ? 1 : 0.55,
              }}
              transition={{ duration: 0.25 }}
              style={{ ...botonSecundario, minHeight: 44, padding: '0 18px' }}
            >
              <Icono
                nombre={trabajando ? 'hourglass_empty' : 'delete_sweep'}
                size={19}
                color={listo ? c.rojo : c.texto4}
              />
              <Cambia clave={trabajando ? 'a' : 'b'}>{trabajando ? 'Borrando…' : 'Vaciar'}</Cambia>
            </motion.button>
          </div>
        </motion.div>
      )}
      </AnimatePresence>

      <div style={{ ...tarjeta, padding: 20, display: 'flex', gap: 11, alignItems: 'flex-start' }}>
        <Icono nombre="architecture" size={20} color={c.texto3} />
        <div style={{ fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
          <strong style={{ color: c.texto }}>El depósito no se desarma desde acá.</strong> Los sectores, los racks que
          ya están acomodados y las posiciones con stock adentro se editan en <em>Planta del depósito</em>, que
          revalida la geometría y no deja un pallet colgado. Lo único de la planta que aparece arriba son las
          ubicaciones que inventó el importador y que nadie acomodó nunca.
        </div>
      </div>
    </div>
  );
}
